/**
 * mediaService — biblioteca de imagens do dono (Mídia, pelo BFF `api/media`) e envio direto ao FileService pelo tíquete.
 * docs/PLANO-EXECUCAO-MIDIA.md, TODO 7.2.
 *
 * Caminho de UMA foto (o padrão de tíquete — a foto nunca passa pela Api):
 *   1. `.heic` → recusa aqui, sem chamar ninguém (Q2: o servidor não converte HEIC);
 *   2. impressão digital = SHA-256 do arquivo ORIGINAL (M13: a mesma foto tirada de novo do celular é "repetida");
 *   3. reduz para no máximo 2.560 px no lado maior (WebP 0,9; sem suporte, JPEG; sem `createImageBitmap`, o original);
 *   4. `POST /media/tickets` — a Mídia confere cota, repetida e ritmo. `jaExiste` → devolve a foto que o dono já tem;
 *   5. envia com `XMLHttpRequest` (progresso) para o `uploadUrl` do tíquete, com `X-Upload-Ticket`.
 * A Mídia marca a foto como pronta sozinha, quando o FileService avisa pela fila.
 *
 * Diferença do plano: a redução vem ANTES do tíquete (o plano punha depois). O tíquete leva o tamanho do arquivo que
 * vai subir, e foto de celular passa fácil de 15 MB (o teto): pedir o tíquete com o original faria a Mídia recusar
 * "TAMANHO" uma foto que, reduzida, cabe. O custo é reduzir à toa uma foto repetida — raro e barato.
 *
 * Respostas de NEGÓCIO (cota cheia, foto em uso, já enviando, formato, muitas de uma vez) não são erro do sistema: elas
 * voltam como `ErroDaMidia` sem passar pelo registro de erros do `api.js` (que mandaria cada "biblioteca cheia" para o
 * painel de erros do Dashboard). 401 continua indo para o login e 5xx continua sendo registrado.
 */
import api from './api';

export const LADO_MAXIMO = 2560;
export const ENVIOS_SIMULTANEOS = 3;

/** Mesma frase que a Mídia devolve (código HEIC) — a tela mostra igual venha de onde vier. */
export const MENSAGEM_HEIC =
  'Fotos .heic (padrão do iPhone) não são aceitas. No iPhone: Ajustes › Câmera › Formatos › Mais Compatível — '
  + 'ou exporte a foto como JPG.';

/** Status que a Mídia usa para recusa de negócio: chegam à tela, não ao painel de erros. */
const STATUS_DE_NEGOCIO = [409, 413, 422, 429];
const respostaEsperada = (status) => (status >= 200 && status < 300) || STATUS_DE_NEGOCIO.includes(status);

/** Erro que a tela sabe ler: `codigo` é o da Mídia/FileService (`COTA`, `EM_USO`, `HEIC`…) ou `REDE`/`ERRO`. */
export class ErroDaMidia extends Error {
  constructor(codigo, mensagem, extra = {}) {
    super(mensagem);
    this.name = 'ErroDaMidia';
    this.codigo = codigo;
    this.status = extra.status ?? null;
    this.usadas = extra.usadas ?? null;
    this.limite = extra.limite ?? null;
    this.usadoEm = extra.usadoEm ?? null;
  }
}

function deResposta(status, corpo) {
  return new ErroDaMidia(corpo?.code || 'ERRO', corpo?.message || 'Não deu certo. Tente de novo.', {
    status, usadas: corpo?.usadas, limite: corpo?.limite, usadoEm: corpo?.usadoEm,
  });
}

/** Erro do axios (5xx, 401, rede) → `ErroDaMidia`. */
function deAxios(e) {
  if (e instanceof ErroDaMidia) return e;
  if (!e?.response) return new ErroDaMidia('REDE', 'Sem conexão com o servidor. Confira a internet e tente de novo.');
  return deResposta(e.response.status, e.response.data);
}

async function chamar(requisicao) {
  let resposta;
  try {
    resposta = await requisicao({ validateStatus: respostaEsperada });
  } catch (e) {
    throw deAxios(e);
  }
  if (resposta.status >= 300) throw deResposta(resposta.status, resposta.data);
  return resposta.data;
}

/**
 * A lista única (M33): fotos do dono primeiro, exemplos do ramo + neutras depois, com a cota.
 * @returns {Promise<{itens: object[], proximo: string|null, cota: {usadas: number, limite: number|null}}>}
 */
export function listar(cursor = null) {
  return chamar((cfg) => api.get('/media', { ...cfg, params: cursor ? { cursor } : {} }));
}

/** Descrição da foto para quem não enxerga (M12). Só foto do dono. */
export function descrever(id, alt) {
  return chamar((cfg) => api.patch(`/media/${id}`, { alt: alt?.trim() || null }, cfg));
}

/**
 * Apaga da biblioteca. Em uso e sem `confirmar` → `ErroDaMidia('EM_USO', …, { usadoEm })` (M37: a tela avisa e
 * pergunta). Com `confirmar`, a Mídia apaga e as páginas perdem a foto (o Pages tira e republica).
 */
export function apagar(id, confirmar = false) {
  return chamar((cfg) => api.delete(`/media/${id}`, { ...cfg, params: { confirmar } }));
}

export function ehHeic(arquivo) {
  return /^image\/hei[cf]/i.test(arquivo?.type || '') || /\.hei[cf]$/i.test(arquivo?.name || '');
}

/** SHA-256 do arquivo, em hexadecimal minúsculo (64 caracteres) — o formato que a Mídia confere. */
export async function impressaoDigital(arquivo) {
  const resumo = await crypto.subtle.digest('SHA-256', await arquivo.arrayBuffer());
  return Array.from(new Uint8Array(resumo), (b) => b.toString(16).padStart(2, '0')).join('');
}

const comExtensao = (nome, ext) => `${(nome || 'foto').replace(/\.[^.]+$/, '')}.${ext}`;

/**
 * Reduz para no máximo `lado` px no lado maior. Foto que já cabe sobe como está (sem recomprimir). Navegador sem
 * `createImageBitmap`, ou que falha ao decodificar → o original (o FileService aceita até 15 MB e reduz lá).
 */
export async function reduzir(arquivo, lado = LADO_MAXIMO) {
  if (typeof createImageBitmap !== 'function') return arquivo;
  let bitmap;
  try {
    bitmap = await createImageBitmap(arquivo);
  } catch {
    return arquivo;
  }
  try {
    const maior = Math.max(bitmap.width, bitmap.height);
    if (maior <= lado) return arquivo;
    const escala = lado / maior;
    const w = Math.round(bitmap.width * escala);
    const h = Math.round(bitmap.height * escala);
    if (typeof OffscreenCanvas === 'function') {
      const tela = new OffscreenCanvas(w, h);
      tela.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      const blob = await tela.convertToBlob({ type: 'image/webp', quality: 0.9 });
      // Navegador que não sabe gerar WebP devolve PNG (pesado) — aí vale o JPEG do canvas comum.
      if (blob?.type === 'image/webp') return new File([blob], comExtensao(arquivo.name, 'webp'), { type: 'image/webp' });
    }
    const tela = document.createElement('canvas');
    tela.width = w;
    tela.height = h;
    tela.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((ok) => tela.toBlob(ok, 'image/jpeg', 0.9));
    return blob ? new File([blob], comExtensao(arquivo.name, 'jpg'), { type: 'image/jpeg' }) : arquivo;
  } catch {
    return arquivo;
  } finally {
    bitmap.close?.();
  }
}

/** Pede o tíquete de UMA foto. Devolve o tíquete (`fileId`, `ticket`, `uploadUrl`…) ou `{ jaExiste: item }`. */
export function pedirTiquete({ nome, bytes, tipo, impressaoDigital: impressao }) {
  return chamar((cfg) => api.post('/media/tickets', { nome, bytes, tipo, impressaoDigital: impressao }, cfg));
}

/**
 * Envia o arquivo direto ao FileService (`POST {uploadUrl}`, multipart campo `file`, cabeçalho `X-Upload-Ticket`).
 * Sem login: quem autoriza é o tíquete. 201 → `{ fileId, largura, altura, cor, bytes, enderecos }`.
 */
export function subir(uploadUrl, ticket, arquivo, aoProgredir, Xhr = globalThis.XMLHttpRequest) {
  return new Promise((ok, falha) => {
    const xhr = new Xhr();
    xhr.open('POST', uploadUrl);
    xhr.setRequestHeader('X-Upload-Ticket', ticket);
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) aoProgredir?.(Math.min(100, Math.round((e.loaded * 100) / e.total)));
      };
    }
    xhr.onload = () => {
      let corpo = null;
      try { corpo = JSON.parse(xhr.responseText); } catch { /* corpo vazio ou HTML de proxy */ }
      if (xhr.status >= 200 && xhr.status < 300) ok(corpo);
      else falha(deResposta(xhr.status, corpo));
    };
    xhr.onerror = () => falha(new ErroDaMidia('REDE', 'A foto não chegou. Confira a internet e tente de novo.'));
    const corpo = new FormData();
    corpo.append('file', arquivo, arquivo.name);
    xhr.send(corpo);
  });
}

/**
 * O caminho inteiro de UMA foto (passos 1–5 do topo). `aoMudar({ etapa, progresso })`: etapa `preparando` |
 * `enviando` | `processando`.
 * @returns {Promise<{tipo: 'enviada', assetId: string, arquivo: object, nome: string} | {tipo: 'jaExiste', imagem: object}>}
 */
export async function enviarFoto(arquivo, { aoMudar } = {}) {
  if (ehHeic(arquivo)) throw new ErroDaMidia('HEIC', MENSAGEM_HEIC);
  aoMudar?.({ etapa: 'preparando', progresso: 0 });
  const impressao = await impressaoDigital(arquivo);
  const final = await reduzir(arquivo);
  const tiquete = await pedirTiquete({ nome: arquivo.name, bytes: final.size, tipo: final.type, impressaoDigital: impressao });
  if (tiquete?.jaExiste) return { tipo: 'jaExiste', imagem: tiquete.jaExiste };
  aoMudar?.({ etapa: 'enviando', progresso: 0 });
  const guardado = await subir(tiquete.uploadUrl, tiquete.ticket, final,
    (progresso) => aoMudar?.({ etapa: 'enviando', progresso }));
  aoMudar?.({ etapa: 'processando', progresso: 100 });
  return { tipo: 'enviada', assetId: tiquete.fileId, arquivo: guardado, nome: arquivo.name };
}

/**
 * Fila de tarefas com no máximo `limite` rodando ao mesmo tempo (3 envios: mais que isso disputa a banda do celular e
 * esbarra no ritmo da Mídia, 20 tíquetes por minuto). Ordem de chegada.
 */
export function criarFila(limite = ENVIOS_SIMULTANEOS) {
  let rodando = 0;
  const espera = [];
  const proxima = () => {
    while (rodando < limite && espera.length) {
      const { tarefa, ok, falha } = espera.shift();
      rodando++;
      Promise.resolve()
        .then(tarefa)
        .then(ok, falha)
        .finally(() => { rodando--; proxima(); });
    }
  };
  return {
    adicionar: (tarefa) => new Promise((ok, falha) => { espera.push({ tarefa, ok, falha }); proxima(); }),
    get rodando() { return rodando; },
    get esperando() { return espera.length; },
  };
}

const mediaService = { listar, descrever, apagar, enviarFoto, criarFila, ehHeic, impressaoDigital, reduzir, pedirTiquete, subir };
export default mediaService;
